import React, { useEffect, useMemo, useState } from 'react';
import styles from './TriageAppointmentsModal.module.css';
import { useDispatch, useSelector } from '../../redux/store';
import { useMsal } from '@azure/msal-react';
import { fetchMyBookingAppointments, cancelBookingAppointment } from '../../redux/slices/bookings';
import { useSnackbar } from 'notistack';

function formatDateRange(startIso, endIso) {
  try {
    const start = startIso ? new Date(startIso) : null;
    const end = endIso ? new Date(endIso) : null;
    if (!start || isNaN(start)) return '';
    const sameDay = end && !isNaN(end) && start.toDateString() === end.toDateString();
    const datePart = start.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    const timeStart = start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const timeEnd = end && !isNaN(end) ? end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
    if (sameDay && timeEnd) return `${datePart} • ${timeStart}–${timeEnd}`;
    if (timeEnd) return `${datePart} • ${timeStart} → ${end.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
    return `${datePart} • ${timeStart}`;
  } catch {
    return '';
  }
}

export default function TriageAppointmentsModal({ isVisible, onClose, onReschedule }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const bookings = useSelector((s) => s.bookings);
  const { accounts } = useMsal();
  const [confirming, setConfirming] = useState(null); // appointment id if confirming
  const [reschedulingId, setReschedulingId] = useState(null); // appointment id if rescheduling
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' | 'past'
  const userEmail = accounts?.[0]?.username || '';

  useEffect(() => {
    if (!isVisible) return;

    // Dispatching a createAsyncThunk returns a promise with an `.abort()` method.
    // On modal close/unmount, abort the in-flight request so we don't handle stale results.
    const promise = dispatch(fetchMyBookingAppointments(userEmail));

    return () => {
      promise?.abort?.();
    };
  }, [dispatch, isVisible, userEmail]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isVisible) {
      setConfirming(null);
      setReschedulingId(null);
    }
  }, [isVisible]);

  const items = useMemo(() => {
    return Array.isArray(bookings.myAppointments) ? bookings.myAppointments : [];
  }, [bookings.myAppointments]);

  const now = useMemo(() => new Date(), []);

  // Split into upcoming and past based on appointment start time (UTC or ISO)
  const { upcoming, past } = useMemo(() => {
    const up = [];
    const pa = [];
    items.forEach((a) => {
      const startIso = a?.start || a?.startDateTime || a?.start_time || a?.start_time_utc;
      const start = startIso ? new Date(startIso) : null;
      if (start && !isNaN(start.getTime())) {
        if (start.getTime() >= now.getTime()) up.push(a);
        else pa.push(a);
      } else {
        // If no valid start, treat as upcoming
        up.push(a);
      }
    });
    // sort upcoming by start asc, past by start desc
    up.sort((x, y) => (new Date(x.start || x.startDateTime || x.start_time || 0)).getTime() - (new Date(y.start || y.startDateTime || y.start_time || 0)).getTime());
    pa.sort((x, y) => (new Date(y.start || y.startDateTime || y.start_time || 0)).getTime() - (new Date(x.start || x.startDateTime || x.start_time || 0)).getTime());
    return { upcoming: up, past: pa };
  }, [items, now]);

  const handleStartConfirm = (appt) => {
    const id = appt?.id || appt?.appointmentId;
    setConfirming(id || null);
    // simple confirmation flow — no extra inputs required
  };

  const handleCancel = async (appt) => {
    const appointmentId = appt?.id || appt?.appointmentId;
    const businessId = appt?.businessId || appt?.business?.id || appt?.business_id;
    if (!appointmentId || !businessId) {
      enqueueSnackbar('Missing appointment or business reference.', { variant: 'error' });
      return;
    }
    const action = await dispatch(cancelBookingAppointment({
      businessId,
      appointmentId,
    }));
    if (action.type.endsWith('fulfilled')) {
      enqueueSnackbar('Appointment cancelled.', { variant: 'success' });
      setConfirming(null);
      // reload list
      dispatch(fetchMyBookingAppointments(userEmail));
    } else {
      enqueueSnackbar(action.payload || action.error?.message || 'Failed to cancel appointment.', { variant: 'error' });
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="My triage appointments">
        <div className={styles.header}>
          <div>My Triage Appointments</div>
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => onClose?.()}>Close</button>
        </div>
        <div className={styles.content}>
          {bookings.myAppointmentsLoading && (
            <div className={styles.empty}>Loading your appointments…</div>
          )}

          {!bookings.myAppointmentsLoading && items.length === 0 && (
            <div className={styles.empty}>No triage appointments found.</div>
          )}

          {!bookings.myAppointmentsLoading && items.length > 0 && (
            <>
              <div className={styles.tabs}>
                <button className={`${styles.tabBtn} ${activeTab === 'upcoming' ? styles.active : ''}`} onClick={() => setActiveTab('upcoming')}>Upcoming ({upcoming.length})</button>
                <button className={`${styles.tabBtn} ${activeTab === 'past' ? styles.active : ''}`} onClick={() => setActiveTab('past')}>Last 30 Days ({past.length})</button>
              </div>

              <div className={styles.appointmentsList}>
                {(activeTab === 'upcoming' ? upcoming : past).map((a) => {
                  const id = a?.id || a?.appointmentId;
                  const businessName = a?.businessName || a?.business?.displayName || a?.business?.name || 'Business';
                  const serviceName = a?.calendarTitle || a?.serviceName || a?.service?.name || 'Service';
                  const startIso = a?.start || a?.startDateTime || a?.start_time || a?.start_time_utc;
                  const endIso = a?.end || a?.endDateTime || a?.end_time || a?.end_time_utc;
                  const when = formatDateRange(startIso, endIso);
                  const projectNumber = a?.projectNumber || a?.project_no || a?.job_no;
                  const isConfirming = confirming === id;
                  const isCancelling = bookings.cancellingAppointmentId === id;
                  const isRescheduling = reschedulingId === id;
                  const showCancel = activeTab === 'upcoming';
                  return (
                    <div key={id || Math.random()} className={styles.row}>
                      <div style={{ fontWeight: 600 }}>{serviceName}</div>
                      <div className={styles.rowMeta}>
                        <div>{businessName}</div>
                        <div>{when}</div>
                        {projectNumber ? <div>Project: {projectNumber}</div> : null}
                        {a?.customerName ? <div>For: {a.customerName}</div> : null}
                      </div>
                      {!isConfirming ? (
                        <div className={styles.rowActions}>
                          {showCancel ? (
                            <>
                              <button
                                className={styles.btn}
                                type="button"
                                disabled={isRescheduling}
                                onClick={() => {
                                  // Let parent modal handle reschedule flow (open booking UI, etc.)
                                  setReschedulingId(id);
                                  if (onReschedule) {
                                    onReschedule(a);
                                  }
                                }}
                              >
                                {isRescheduling ? 'Opening…' : 'Reschedule'}
                              </button>
                              <button
                                className={styles.btn}
                                type="button"
                                onClick={() => handleStartConfirm(a)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <div className={styles.rowActions}>
                            <button
                              className={styles.btn}
                              disabled={isCancelling}
                              onClick={() => handleCancel(a)}
                            >
                              {isCancelling ? 'Cancelling…' : 'Confirm cancel'}
                            </button>
                            <button
                              className={`${styles.btn} ${styles.btnSecondary}`}
                              onClick={() => { setConfirming(null); }}
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className={styles.footer}>
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => onClose?.()}>Done</button>
        </div>
      </div>
    </div>
  );
}


